import React from 'react'
import { useMediaQuery } from 'react-responsive'
import { ReactSVG } from 'react-svg'
import Skeleton from 'react-loading-skeleton'

import Card from './Card'
import CustomLink from './CustomLink'
import BtnShare from './BtnShare'
import Dropdown from './Dropdown'
import DropdownShare from './DropdownShare'
import Chip from './Chip'
import Participants from './Participants'
import { type ArticleData } from '../server/utils/getArticlesData'
import Favorite from './Favorite'
import { axiosInstance } from '../hooks/useAxios'
import { addArticleToFavorite } from '../api/addArticleToFavorite'
import { removeArticleFromFavorite } from '../api/removeArticleFromFavorite'
import Participation from './Participation'
import { joinEvent } from '../api/joinEvent'
import { leaveEvent } from '../api/leaveEvent'
import classNames from 'classnames'

interface Props extends ArticleData {
  loading: boolean
}

const CommunityDetailCard: React.FC<Props> = ({
  id,
  loading,
  title,
  tags,
  informationItems,
  socials,
  shareLinks,
  participants,
  detailPicture,
  favorite,
  participation,
  description,
  hasParticipation
}) => {
  const isTablet = useMediaQuery({
    query: '(max-width: 1023px)'
  })

  const favoriteHandler = async (value: boolean, id: string) => {
    if (value) {
      await axiosInstance(addArticleToFavorite('communities', id))
    } else {
      await axiosInstance(removeArticleFromFavorite('communities', id))
    }
  }

  const participationHandler = async (value: boolean, id: string) => {
    if (value) {
      await axiosInstance(joinEvent('communities', id))
    } else {
      await axiosInstance(leaveEvent('communities', id))
    }
  }

  return (
    <>
      {loading ? (
        <Card className="community-detail-card">
          <div className="community-detail-card__image">
            {isTablet ? (
              <Skeleton height="18rem" />
            ) : (
              <Skeleton width="52rem" height="100%" />
            )}
          </div>
          <div className="community-detail-card__main">
            <div className="community-detail-card__head">
              <Skeleton height="5rem" />
              <div className="community-detail-card__head-wrapper">
                <div className="community-detail-card__tags">
                  {Array(3)
                    .fill(1)
                    .map((_, index) => (
                      <Skeleton key={index} height="3rem" width="10rem" />
                    ))}
                </div>
              </div>
              <Skeleton />
            </div>
            <div className="community-detail-card__body">
              <Skeleton />
              <ul className="community-detail-card__socials">
                {Array(2)
                  .fill(1)
                  .map((_, index) => (
                    <Skeleton key={index} height="2rem" width="10rem" />
                  ))}
              </ul>
            </div>
            <div className="community-detail-card__footer">
              <Skeleton height="5rem" />
              <Skeleton width="20rem" height="4.5rem" />
            </div>
          </div>
        </Card>
      ) : (
        <Card className="community-detail-card">
          <div className="community-detail-card__image">
            <Favorite
              value={!!favorite}
              className="community-detail-card__bookmark"
              handler={(value) => {
                return favoriteHandler(value, id as string)
              }}
            ></Favorite>
            <img src={detailPicture} alt="image" />
          </div>
          <div className="community-detail-card__main">
            {participants?.items.length ? (
              <Participants
                className="community-detail-card__participants"
                {...participants}
              />
            ) : null}
            <div className="community-detail-card__head">
              <h1
                className={classNames([
                  'community-detail-card__title',
                  { 'text-h2': title.length > 50 }
                ])}
              >
                {title}
              </h1>
              <div className="community-detail-card__head-wrapper">
                {tags && (
                  <div className="community-detail-card__tags">
                    {tags.map((tag, index) => (
                      <Chip key={index} color={tag.color}>
                        {tag.label}
                      </Chip>
                    ))}
                  </div>
                )}
                <Dropdown
                  className="event-detail-card__share-mobile"
                  trigger={(open) => (
                    <BtnShare onClick={open}>Поделиться</BtnShare>
                  )}
                  render={(close) => (
                    <DropdownShare items={shareLinks} onClick={close} />
                  )}
                />
              </div>
              {description ? (
                <p
                  className={
                    'community-detail-card__description text-body-small text-secondary-color'
                  }
                >
                  {description}
                </p>
              ) : null}
            </div>
            <div className="community-detail-card__body">
              {informationItems && (
                <ul className="community-detail-card__info">
                  {informationItems.map(({ icon, text }, index) => (
                    <li key={index}>
                      <ReactSVG src={`/i/svg/${icon}.svg`} wrapper="span" />
                      {text}
                    </li>
                  ))}
                </ul>
              )}
              {socials && (
                <ul className="community-detail-card__socials">
                  {socials.map((link, index) => (
                    <li key={index}>
                      <CustomLink {...link} type="social" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="community-detail-card__footer">
              {participants?.items.length ? (
                <Participants
                  className="community-detail-card__participants-footer"
                  {...participants}
                />
              ) : null}

              <div className="community-detail-card__actions">
                {hasParticipation ? (
                  <Participation
                    value={!!participation}
                    handler={(value) => {
                      return participationHandler(value, id as string)
                    }}
                  ></Participation>
                ) : null}

                <Dropdown
                  className="event-detail-card__share"
                  trigger={(open) => (
                    <BtnShare onClick={open}>Поделиться</BtnShare>
                  )}
                  render={(close) => (
                    <DropdownShare items={shareLinks} onClick={close} />
                  )}
                />
              </div>
            </div>
          </div>
        </Card>
      )}
    </>
  )
}

export default CommunityDetailCard
